import { useMemo, useState } from 'react'
import {
  dashboardMonths,
  dashboardOfficeOptions,
  dashboardSeries,
  dashboardVisitSeries,
  dashboardYears,
  type DashboardChartKey,
} from '../../mock/legacyDashboard'
import './LegacyDashboard.css'

type FilterState = { all: boolean; office: string; year: number; month: string }
type DashboardCard = {
  key: DashboardChartKey
  title: string
  kind: 'bar' | 'line' | 'doughnut'
  color: string
  detailPath: string
  legend: string
}

const currentYear = 2026

const cards: DashboardCard[] = [
  { key: 'customer', title: '고객 통계', kind: 'bar', color: '#01c653', detailPath: '/v2/visit-status/', legend: '월별 전체 고객통계' },
  { key: 'schedule', title: '스케쥴 통계', kind: 'bar', color: '#ff164b', detailPath: '/v1/schedule/daily/', legend: '월별 전체 스케줄통계' },
  { key: 'sales', title: '매출 통계', kind: 'line', color: '#1ab7c4', detailPath: '/v1/sales_statistics', legend: '월별 전체 매출통계' },
  { key: 'balance', title: '계약관리 통계', kind: 'bar', color: '#087ee6', detailPath: '/v1/statistics_by_counselor', legend: '월별 전체 잔금' },
  { key: 'visit1', title: '방문동기1 통계', kind: 'doughnut', color: '#ff174b', detailPath: '/v2/visit-status/', legend: '' },
  { key: 'visit2', title: '방문동기2 통계', kind: 'doughnut', color: '#ff72a1', detailPath: '/v2/visit-status/', legend: '' },
]

const initialFilters = Object.fromEntries(cards.map(({ key }) => [key, {
  all: true,
  office: 'total_hq',
  year: currentYear,
  month: key === 'visit1' || key === 'visit2' ? '8' : 'total',
}])) as Record<DashboardChartKey, FilterState>

function rotatedSeries(values: number[], year: number, month: string) {
  const shift = Math.abs(year - currentYear + (month === 'total' ? 0 : Number(month))) % values.length
  return [...values.slice(shift), ...values.slice(0, shift)]
}

function DashboardFilters({ card, value, onChange, onSearch }: {
  card: DashboardCard
  value: FilterState
  onChange: (next: FilterState) => void
  onSearch: () => void
}) {
  const visit = card.kind === 'doughnut'
  return <div className="search_div">
    {!visit && <div className="chart_title">{card.title}</div>}
    <div className="select_office d01-filter-line">
      <span>지점선택</span>
      {visit
        ? <label className="g_all_check"><input type="checkbox" checked={value.all} onChange={(event) => onChange({ ...value, all: event.target.checked, office: event.target.checked ? 'total_hq' : value.office })} />전체</label>
        : <span className="all_btn"><label className="g_all_check"><input type="checkbox" checked={value.all} onChange={(event) => onChange({ ...value, all: event.target.checked, office: event.target.checked ? 'total_hq' : value.office })} />전체</label></span>}
      {visit && <span className="d01-inline-space" aria-hidden="true" />}
      <span className="office_title">직영점</span>
      <select aria-label={`${card.title} 직영점`} className="g_input" value={value.office} onChange={(event) => onChange({ ...value, all: false, office: event.target.value })}>
        {dashboardOfficeOptions.map((option) => <option value={option.value} key={option.value}>{option.label}</option>)}
      </select>
    </div>
    <div className="d01-filter-line">
      <span>기간검색</span>
      <select aria-label={`${card.title} 연도`} className="g_input" value={value.year} onChange={(event) => onChange({ ...value, year: Number(event.target.value) })}>
        {dashboardYears.map((year) => <option value={year} key={year}>{year}년</option>)}
      </select>
      <select aria-label={`${card.title} 월`} className="g_input" value={value.month} onChange={(event) => onChange({ ...value, month: event.target.value })}>
        {(visit ? dashboardMonths.slice(1) : dashboardMonths).map((month) => <option value={month.value} key={month.value}>{month.label}</option>)}
      </select>
      <button type="button" className="g_btn search size_1" onClick={onSearch}>검색</button>
    </div>
  </div>
}

function CartesianChart({ card, year, month, onOpen }: { card: DashboardCard; year: number; month: string; onOpen: () => void }) {
  const values = rotatedSeries(dashboardSeries[card.key as keyof typeof dashboardSeries], year, month)
  const left = 22
  const top = 32
  const bottom = 196
  const right = 447
  const step = (right - left) / 12
  const y = (value: number) => bottom - (Math.min(9, value) / 9) * (bottom - top)
  const linePoints = values.map((value, index) => `${left + step * index + step / 2},${y(value)}`).join(' ')
  const areaPoints = `${left + step / 2},${bottom} ${linePoints} ${left + step * 11 + step / 2},${bottom}`
  return <button className="d01-chart-action" type="button" aria-label={`${card.title} 상세 화면 열기`} onClick={onOpen}>
    <svg className="d01-cartesian-chart" width="448" height="224" viewBox="0 0 448 224" role="img" aria-label={`${card.title} Mock 차트`}>
      <defs>
        <linearGradient id={`d01-gradient-${card.key}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#03d5e4" stopOpacity=".8" />
          <stop offset=".55" stopColor="#03d5e4" stopOpacity=".25" />
          <stop offset="1" stopColor="#f25774" stopOpacity="0" />
        </linearGradient>
      </defs>
      <g className="d01-legend"><rect x="132" y="4" width="41" height="12" rx="1" fill={card.color} /><text x="180" y="14">{year}년 {card.legend}</text></g>
      {Array.from({ length: 10 }, (_, index) => {
        const gridY = top + ((bottom - top) / 9) * index
        return <g key={`y-${index}`}><line x1={left} y1={gridY} x2={right} y2={gridY} /><text x="10" y={gridY + 4}>{9 - index}</text></g>
      })}
      {Array.from({ length: 13 }, (_, index) => <line className="d01-v-grid" x1={left + step * index} y1={top} x2={left + step * index} y2={bottom} key={`x-${index}`} />)}
      {card.kind === 'bar' && values.map((value, index) => {
        const barY = y(value)
        return <rect key={index} x={left + step * index + 11} y={barY} width="14" height={bottom - barY} rx="7" fill={card.color}><title>{`${index + 1}월 Mock ${value}`}</title></rect>
      })}
      {card.kind === 'line' && <><polygon points={areaPoints} fill={`url(#d01-gradient-${card.key})`} /><polyline points={linePoints} fill="none" stroke={card.color} strokeWidth="1.3" />{values.map((value, index) => <circle cx={left + step * index + step / 2} cy={y(value)} r="3" fill="#65dce3" stroke={card.color} key={index}><title>{`${index + 1}월 Mock ${value}`}</title></circle>)}</>}
      {Array.from({ length: 12 }, (_, index) => <text className="d01-month-label" x={left + step * index + step / 2} y="216" textAnchor="middle" key={index}>{index + 1}월</text>)}
    </svg>
  </button>
}

function DoughnutChart({ card, labelsVisible, onOpen }: { card: DashboardCard; labelsVisible: boolean; onOpen: () => void }) {
  const data = dashboardVisitSeries[card.key as 'visit1' | 'visit2']
  const colors = ['#ff174b', '#ff8c56', '#01c653', '#00b3f7', '#03d5e4', '#664cf0']
  const total = data.reduce((sum, item) => sum + item.value, 0)
  let offset = 0
  return <button className="d01-chart-action d01-doughnut-action" type="button" aria-label={`${card.title} 상세 화면 열기`} onClick={onOpen}>
    <svg className="d01-doughnut-chart" width="300" height="300" viewBox="0 0 300 300" role="img" aria-label={`${card.title} Mock 차트`}>
      <g transform="rotate(-90 150 164)">
        {data.map((item, index) => {
          const percent = item.value / total
          const dash = `${percent * 678.58} ${678.58 - percent * 678.58}`
          const segment = <circle key={item.label} cx="150" cy="164" r="108" fill="none" stroke={colors[index]} strokeWidth="60" strokeDasharray={dash} strokeDashoffset={-offset * 678.58}><title>{`${item.label} ${item.value}`}</title></circle>
          offset += percent
          return segment
        })}
      </g>
      {labelsVisible && <g className="d01-doughnut-legend">{data.map((item, index) => <g transform={`translate(${20 + (index % 2) * 135} ${14 + Math.floor(index / 2) * 15})`} key={item.label}><rect width="10" height="10" fill={colors[index]} /><text x="14" y="9">{item.label}</text></g>)}</g>}
    </svg>
  </button>
}

function DashboardCardView({ card, value, onChange, onNotice }: { card: DashboardCard; value: FilterState; onChange: (next: FilterState) => void; onNotice: (message: string) => void }) {
  const [labelsVisible, setLabelsVisible] = useState(false)
  const [applied, setApplied] = useState(value)
  const openDetail = () => { window.location.assign(card.detailPath) }
  return <section className={`container ${card.kind === 'doughnut' ? 'circle_chart' : ''}`} data-chart={card.key}>
    {card.kind === 'doughnut' && <div className="chart_title d01-circle-title"><div>{card.title}</div><div className="d01-total-count">전체 Count: {dashboardVisitSeries[card.key as 'visit1' | 'visit2'].reduce((sum, item) => sum + item.value, 0)}</div></div>}
    <DashboardFilters card={card} value={value} onChange={onChange} onSearch={() => { setApplied(value); onNotice(`${card.title} Mock 통계를 조회했습니다.`) }} />
    {card.kind === 'doughnut' && <><span className="d01-label-spacer" aria-hidden="true" /><button type="button" className="g_btn size_2 d01-label-button" onClick={() => setLabelsVisible(false)}>레이블 숨기기</button>{' '}<button type="button" className="g_btn size_2 d01-label-button" onClick={() => setLabelsVisible(true)}>레이블 표시하기</button></>}
    {card.kind === 'doughnut'
      ? <DoughnutChart card={card} labelsVisible={labelsVisible} onOpen={openDetail} />
      : <CartesianChart card={card} year={applied.year} month={applied.month} onOpen={openDetail} />}
  </section>
}

export function LegacyDashboard() {
  const [filters, setFilters] = useState(initialFilters)
  const [notice, setNotice] = useState('')
  const byKey = useMemo(() => Object.fromEntries(cards.map((card) => [card.key, card])) as Record<DashboardChartKey, DashboardCard>, [])
  const renderCard = (key: DashboardChartKey) => <DashboardCardView key={key} card={byKey[key]} value={filters[key]} onChange={(next) => setFilters((current) => ({ ...current, [key]: next }))} onNotice={setNotice} />
  return <div className="legacy-d01">
    <div className="main_title margin_bottom_10">대시보드</div>
    <div className="container_wrap">
      <div className="container_left"><div className="two_container">{renderCard('customer')}{renderCard('schedule')}</div><div className="one_container">{renderCard('sales')}</div></div>
      <div className="container_right"><div className="two_container">{renderCard('balance')}{renderCard('visit1')}</div><div className="one_container">{renderCard('visit2')}</div></div>
    </div>
    {notice && <div className="d01-notice" role="status">{notice}<button type="button" aria-label="알림 닫기" onClick={() => setNotice('')}>×</button></div>}
  </div>
}
